/** Promo codes admin (Phase 4.2). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

type DiscountType = "percent" | "fixed" | "free_delivery";

interface PromoCode {
  id: number;
  code: string;
  description: string | null;
  discount_type: DiscountType;
  discount_value: number;
  min_order_total: number;
  max_discount: number | null;
  max_uses: number | null;
  max_uses_per_client: number;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PromoCodesResponse {
  data: PromoCode[];
  pagination: { total: number };
}

const DISCOUNT_TYPES: DiscountType[] = ["percent", "fixed", "free_delivery"];

export function PromoCodesListPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-promo-codes"],
    queryFn: async () => {
      const { data } = await api.get<PromoCodesResponse>("/promo-codes", {
        params: { per_page: 100 },
      });
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/promo-codes/${id}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить промокод?",
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      await api.patch(`/promo-codes/${id}`, { is_active });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] }),
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? t("common.toast.errorOccurred")),
  });
  const togglingId = toggleActiveMutation.isPending
    ? (toggleActiveMutation.variables?.id ?? null)
    : null;

  const items = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">{t("nav.promoCodes")}</h1>
        <Button onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {t("common.create")}
        </Button>
      </div>

      {adding && <PromoForm onClose={() => setAdding(false)} />}
      {editing && (
        <PromoForm initial={editing} onClose={() => setEditing(null)} />
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("promoCodes.empty")}
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => (
            <Card key={p.id}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-medium text-base">{p.code}</span>
                      <Badge variant="secondary">
                        {t(`promoCodes.types.${p.discount_type}`)}
                      </Badge>
                      <Badge variant="outline">
                        {p.discount_type === "percent"
                          ? `${p.discount_value}%`
                          : p.discount_type === "fixed"
                          ? `${(p.discount_value / 100).toLocaleString("ru-RU")} ${t("promoCodes.sum")}`
                          : t("promoCodes.types.free_delivery")}
                      </Badge>
                      {!p.is_active && (
                        <Badge variant="destructive">
                          {t("promoCodes.inactive")}
                        </Badge>
                      )}
                    </div>
                    {p.description && (
                      <div className="text-sm text-muted-foreground">
                        {p.description}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
                      <span>
                        {t("promoCodes.usesLabel")}: {p.uses_count}
                        {p.max_uses != null && ` / ${p.max_uses}`}
                      </span>
                      <span>
                        {t("promoCodes.perClient")}: {p.max_uses_per_client}
                      </span>
                      {p.min_order_total > 0 && (
                        <span>
                          {t("promoCodes.minOrder")}:{" "}
                          {(p.min_order_total / 100).toLocaleString("ru-RU")}{" "}
                          {t("promoCodes.sum")}
                        </span>
                      )}
                      {p.max_discount != null && (
                        <span>
                          {t("promoCodes.maxDiscount")}:{" "}
                          {(p.max_discount / 100).toLocaleString("ru-RU")}{" "}
                          {t("promoCodes.sum")}
                        </span>
                      )}
                      {p.valid_until && (
                        <span>
                          {t("promoCodes.until")}:{" "}
                          {new Date(p.valid_until).toLocaleDateString("ru-RU")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={p.is_active}
                      disabled={togglingId === p.id}
                      onCheckedChange={(is_active) =>
                        toggleActiveMutation.mutate({ id: p.id, is_active })
                      }
                    />
                    {togglingId === p.id && (
                      <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(p)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => askDelete(p.id, p.code)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {deleteDialog}
    </div>
  );
}

function PromoForm({
  initial,
  onClose,
}: {
  initial?: PromoCode;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!initial;

  // Internal state — деньги в СУМАХ, конвертация в тийины при отправке
  const [form, setForm] = useState({
    code: initial?.code ?? "",
    description: initial?.description ?? "",
    discount_type: (initial?.discount_type ?? "percent") as DiscountType,
    discount_value: initial?.discount_value ?? 10,
    min_order_total_sum: (initial?.min_order_total ?? 0) / 100,
    max_discount_sum: initial?.max_discount != null ? initial.max_discount / 100 : "",
    max_uses: initial?.max_uses ?? "",
    max_uses_per_client: initial?.max_uses_per_client ?? 1,
    valid_until: initial?.valid_until ? initial.valid_until.slice(0, 10) : "",
    is_active: initial?.is_active ?? true,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        description: form.description || null,
        discount_type: form.discount_type,
        discount_value:
          form.discount_type === "fixed"
            ? Math.round(Number(form.discount_value) * 100)
            : Number(form.discount_value),
        min_order_total: Math.round(Number(form.min_order_total_sum || 0) * 100),
        max_discount:
          form.max_discount_sum !== ""
            ? Math.round(Number(form.max_discount_sum) * 100)
            : null,
        max_uses: form.max_uses !== "" ? Number(form.max_uses) : null,
        max_uses_per_client: Number(form.max_uses_per_client),
        valid_until: form.valid_until
          ? new Date(form.valid_until).toISOString()
          : null,
        is_active: form.is_active,
      };
      if (isEdit) {
        await api.patch(`/promo-codes/${initial!.id}`, payload);
      } else {
        await api.post("/promo-codes", { ...payload, code: form.code });
      }
    },
    onSuccess: () => {
      toast.success(
        isEdit ? t("common.toast.saved") : t("promoCodes.created"),
      );
      queryClient.invalidateQueries({ queryKey: ["admin-promo-codes"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.error?.message ?? t("common.toast.errorOccurred"),
      ),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {isEdit
            ? `${t("common.edit")}: ${initial!.code}`
            : t("promoCodes.newTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isEdit && (
          <div className="space-y-1.5">
            <Label>{t("promoCodes.code")}</Label>
            <Input
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              placeholder="SUMMER10"
              className="font-mono"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("promoCodes.type")}</Label>
            <Select
              value={form.discount_type}
              onValueChange={(discount_type: DiscountType) =>
                setForm({ ...form, discount_type })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_TYPES.map((dt) => (
                  <SelectItem key={dt} value={dt}>
                    {t(`promoCodes.types.${dt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.discount_type !== "free_delivery" && (
            <div className="space-y-1.5">
              <Label>
                {form.discount_type === "percent"
                  ? t("promoCodes.percentValue")
                  : t("promoCodes.fixedValueSum")}
              </Label>
              <Input
                type="number"
                value={form.discount_value}
                onChange={(e) =>
                  setForm({ ...form, discount_value: Number(e.target.value) })
                }
                min={1}
                max={form.discount_type === "percent" ? 100 : undefined}
              />
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{t("promoCodes.description")}</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("promoCodes.minOrderSum")}</Label>
            <Input
              type="number"
              value={form.min_order_total_sum}
              onChange={(e) =>
                setForm({ ...form, min_order_total_sum: Number(e.target.value) })
              }
              min={0}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("promoCodes.maxDiscountSum")}</Label>
            <Input
              type="number"
              value={form.max_discount_sum}
              onChange={(e) =>
                setForm({ ...form, max_discount_sum: e.target.value as any })
              }
              min={0}
              placeholder={t("promoCodes.noLimit")}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("promoCodes.maxUses")}</Label>
            <Input
              type="number"
              value={form.max_uses}
              onChange={(e) =>
                setForm({ ...form, max_uses: e.target.value as any })
              }
              min={1}
              placeholder={t("promoCodes.noLimit")}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("promoCodes.maxUsesPerClient")}</Label>
            <Input
              type="number"
              value={form.max_uses_per_client}
              onChange={(e) =>
                setForm({ ...form, max_uses_per_client: Number(e.target.value) })
              }
              min={1}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("promoCodes.validUntil")}</Label>
          <Input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_active}
            onCheckedChange={(is_active) => setForm({ ...form, is_active })}
          />
          <Label>{t("promoCodes.isActive")}</Label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              saveMutation.isPending || (!isEdit && !form.code.trim())
            }
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
